import { useEffect, useState } from "react";
import api from "../utils/axios";
import {
  FiCheckCircle,
  FiXCircle,
  FiEdit2,
  FiTrash2,
  FiPlus,
  FiUploadCloud,
  FiCopy,
  FiCheck,
  FiEye,
  FiRefreshCw,
  FiSmartphone,
  FiX
} from "react-icons/fi";

const GATEWAYS = [
  {
    id: "BKASH",
    name: "bKash",
    tag: "Mobile Banking",
    defaultType: "bKash Personal",
    color: "#E2136E",
    bgLight: "bg-pink-50/70",
    hoverBorder: "hover:border-[#E2136E]",
    borderActive: "border-[#E2136E]",
    textActive: "text-[#E2136E]",
    badgeBg: "bg-pink-100 text-[#E2136E] border-pink-200",
    logo: "https://static.vecteezy.com/system/resources/previews/068/842/080/non_2x/bkash-logo-horizontal-mobile-banking-app-icon-emblem-transparent-background-free-png.png",
  },
  {
    id: "NAGAD",
    name: "Nagad",
    tag: "Digital Payment",
    defaultType: "Nagad Personal",
    color: "#F7941D",
    bgLight: "bg-orange-50/70",
    hoverBorder: "hover:border-[#F7941D]",
    borderActive: "border-[#F7941D]",
    textActive: "text-[#F7941D]",
    badgeBg: "bg-orange-100 text-[#F7941D] border-orange-200",
    logo: "https://static.vecteezy.com/system/resources/previews/068/894/449/non_2x/nagan-logo-horizontal-bangla-mobile-banking-app-icon-transparent-background-free-png.png",
  },
  {
    id: "ROCKET",
    name: "Rocket",
    tag: "DBBL Banking",
    defaultType: "Rocket Personal",
    color: "#8C3494",
    bgLight: "bg-purple-50/70",
    hoverBorder: "hover:border-[#8C3494]",
    borderActive: "border-[#8C3494]",
    textActive: "text-[#8C3494]",
    badgeBg: "bg-purple-100 text-[#8C3494] border-purple-200",
    logo: "https://payget.xyz/images/payment/rocket.png",
  },
  {
    id: "UPAY",
    name: "Upay",
    tag: "UCB FinTech",
    defaultType: "Upay Personal",
    color: "#005C8A",
    bgLight: "bg-blue-50/70",
    hoverBorder: "hover:border-[#005C8A]",
    borderActive: "border-[#005C8A]",
    textActive: "text-[#005C8A]",
    badgeBg: "bg-blue-100 text-[#005C8A] border-blue-200",
    logo: "https://static.vecteezy.com/system/resources/previews/068/706/007/non_2x/upay-logo-color-mobile-banking-app-icon-free-png.png",
  },
  {
    id: "USDT",
    name: "USDT",
    tag: "Crypto (TRC20)",
    defaultType: "USDT (TRC20)",
    color: "#26A17B",
    bgLight: "bg-emerald-50/70",
    hoverBorder: "hover:border-[#26A17B]",
    borderActive: "border-[#26A17B]",
    textActive: "text-[#26A17B]",
    badgeBg: "bg-emerald-100 text-[#26A17B] border-emerald-200",
    logo: "https://cryptologos.cc/logos/tether-usdt-logo.png",
    isCrypto: true,
  },
];

export default function PaymentMethods() {
  const [methods, setMethods] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [notification, setNotification] = useState(null);

  const [form, setForm] = useState({
    payment_settings_id: 1,
    user_id: 1,
    payment_type: "BKASH",
    bank_name: "bKash Personal",
    account_holder_name: "",
    bank_account_number: "",
    qr_file: null,
    currency: "BDT",
  });

  const showNotification = (type, text) => {
    setNotification({ type, text });
    setTimeout(() => {
      setNotification(null);
    }, 4000);
  };

  /* ================= FETCH ================= */
  const loadMethods = async () => {
    setLoading(true);
    try {
      const res = await api.get("/payment-methods");
      setMethods(res.data?.data || []);
    } catch (error) {
      console.error("Error loading payment methods:", error);
      showNotification("error", "Failed to load payment methods.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMethods();
  }, []);

  /* ================= GATEWAY SELECT ================= */
  const handleGatewayChange = (gwId) => {
    const gw = GATEWAYS.find((g) => g.id === gwId);
    const isCrypto = gw?.isCrypto || gwId === "USDT" || gwId === "crypto";
    setForm((prev) => ({
      ...prev,
      payment_type: gwId,
      bank_name: gw?.defaultType || (isCrypto ? "USDT (TRC20)" : `${gw?.name} Personal`),
      currency: isCrypto ? "USDT" : "BDT",
      account_holder_name: isCrypto ? (prev.account_holder_name || "Pori420 USDT TRC20") : prev.account_holder_name,
    }));
  };

  /* ================= FILE HANDLER ================= */
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setForm((prev) => ({ ...prev, qr_file: file }));
    setPreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = () => {
    setForm((prev) => ({ ...prev, qr_file: null }));
    setPreview(null);
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const isCrypto = form.payment_type === "USDT" || form.payment_type?.toLowerCase() === "crypto";

    if (!form.bank_account_number.trim()) {
      showNotification("error", isCrypto ? "Please enter the USDT Wallet Address." : "Please enter the Account Number / RT Number.");
      return;
    }

    setSubmitLoading(true);

    try {
      const payload = new FormData();
      payload.append("payment_settings_id", form.payment_settings_id || 1);
      payload.append("user_id", form.user_id || 1);
      payload.append("payment_type", isCrypto ? "crypto" : form.payment_type);
      payload.append("bank_name", form.bank_name || (isCrypto ? "USDT (TRC20)" : `${form.payment_type} Personal`));
      payload.append("account_holder_name", form.account_holder_name.trim() || (isCrypto ? "Pori420 USDT TRC20" : "Pori420 Admin"));
      payload.append("bank_account_number", form.bank_account_number.trim());
      payload.append("currency", isCrypto ? "USDT" : "BDT");
      payload.append("ifsc_code", isCrypto ? "TRC20" : "");
      payload.append("upi_id", form.bank_account_number.trim());
      payload.append("wallet_address", form.bank_account_number.trim());

      if (form.qr_file) {
        payload.append("qr_file", form.qr_file);
      }

      if (editingId) {
        await api.put(`/payment-methods/${editingId}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showNotification("success", "Payment Gateway updated successfully!");
      } else {
        await api.post("/payment-methods", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showNotification("success", "Payment Gateway added successfully!");
      }

      resetForm();
      loadMethods();
    } catch (err) {
      console.error(err);
      showNotification("error", err.response?.data?.message || err.response?.data?.error || "An error occurred while saving.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      payment_settings_id: 1,
      user_id: 1,
      payment_type: "BKASH",
      bank_name: "bKash Personal",
      account_holder_name: "",
      bank_account_number: "",
      qr_file: null,
      currency: "BDT",
    });
    setPreview(null);
    setEditingId(null);
  };

  /* ================= EDIT ================= */
  const handleEdit = (m) => {
    setEditingId(m.id);
    setPreview(m.qr_image || null);

    const isCrypto = m.payment_type?.toLowerCase() === "crypto" || m.currency === "USDT" || m.payment_type?.toUpperCase() === "USDT";
    const matchedGw = isCrypto
      ? GATEWAYS.find((g) => g.id === "USDT")
      : GATEWAYS.find((g) => g.id.toLowerCase() === m.payment_type?.toLowerCase());

    const gwId = matchedGw ? matchedGw.id : (isCrypto ? "USDT" : "BKASH");

    setForm({
      payment_settings_id: m.payment_settings_id || 1,
      user_id: m.user_id || 1,
      payment_type: gwId,
      bank_name: m.bank_name || (matchedGw ? matchedGw.defaultType : (isCrypto ? "USDT (TRC20)" : "bKash Personal")),
      account_holder_name: m.account_holder_name || "",
      bank_account_number: m.bank_account_number || m.wallet_address || "",
      qr_file: null,
      currency: isCrypto ? "USDT" : (m.currency || "BDT"),
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this payment gateway?")) return;
    try {
      await api.delete(`/payment-methods/${id}`);
      showNotification("success", "Payment gateway deleted.");
      loadMethods();
    } catch (err) {
      console.error(err);
      showNotification("error", "Failed to delete payment gateway.");
    }
  };

  /* ================= STATUS ================= */
  const toggleStatus = async (id, is_active) => {
    try {
      await api.patch(`/payment-methods/${id}/status`, { is_active });
      showNotification("success", `Gateway ${is_active ? "Enabled" : "Disabled"}`);
      loadMethods();
    } catch (err) {
      console.error(err);
      showNotification("error", "Failed to update status.");
    }
  };

  /* ================= COPY ================= */
  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getGatewayMeta = (type) => {
    const normalized = (type || "").toUpperCase();
    if (normalized === "CRYPTO" || normalized === "USDT" || normalized.includes("TRC20") || normalized.includes("TETHER")) {
      return GATEWAYS.find((g) => g.id === "USDT") || {
        id: "USDT",
        name: "USDT",
        tag: "Crypto (TRC20)",
        color: "#26A17B",
        bgLight: "bg-emerald-50/70",
        borderActive: "border-[#26A17B]",
        textActive: "text-[#26A17B]",
        badgeBg: "bg-emerald-100 text-[#26A17B] border-emerald-200",
        logo: "https://cryptologos.cc/logos/tether-usdt-logo.png",
        isCrypto: true,
      };
    }
    return (
      GATEWAYS.find((g) => g.id === normalized || g.name.toUpperCase() === normalized) || {
        id: normalized,
        name: type || "Gateway",
        tag: "Payment Gateway",
        color: "#10b981",
        bgLight: "bg-gray-50",
        borderActive: "border-gray-300",
        textActive: "text-gray-700",
        badgeBg: "bg-gray-100 text-gray-700 border-gray-200",
        logo: null,
      }
    );
  };

  const activeCount = methods.filter((m) => m.is_active).length;
  const isFormCrypto = form.payment_type === "USDT" || form.payment_type?.toLowerCase() === "crypto";

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-10">
      {/* Toast Notification */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg border text-sm font-medium transition-all ${notification.type === "success"
            ? "bg-green-50 text-green-800 border-green-200"
            : "bg-red-50 text-red-800 border-red-200"
            }`}
        >
          {notification.type === "success" ? (
            <FiCheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
          ) : (
            <FiXCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
          )}
          <span>{notification.text}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gradient-to-r from-gray-900 via-slate-800 to-gray-900 p-6 rounded-2xl text-white shadow-md">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
              Payment Gateways & Crypto
            </span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight">Payment Gateways</h1>
          <p className="text-xs text-gray-400 mt-1">
            Configure active deposit gateways: <strong>BKASH</strong>, <strong>NAGAD</strong>, <strong>ROCKET</strong>, <strong>UPAY</strong>, and <strong>USDT (Crypto)</strong>.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="bg-white/10 backdrop-blur-sm border border-white/10 px-4 py-2 rounded-xl text-center">
            <p className="text-[11px] text-gray-300 font-medium">Active Gateways</p>
            <p className="text-lg font-bold text-emerald-400">{activeCount} / {methods.length}</p>
          </div>
          <button
            onClick={loadMethods}
            disabled={loading}
            className="flex items-center gap-1.5 px-3.5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl text-xs font-semibold transition-all border border-white/15"
            title="Refresh List"
          >
            <FiRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Gateway Quick Selector Preview Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5">
        {GATEWAYS.map((gw) => {
          const isSelected = form.payment_type === gw.id || (gw.id === "USDT" && form.payment_type?.toLowerCase() === "crypto");
          const count = methods.filter((m) => {
            const mType = (m.payment_type || "").toUpperCase();
            if (gw.id === "USDT") return mType === "USDT" || mType === "CRYPTO" || m.currency === "USDT";
            return mType === gw.id;
          }).length;

          return (
            <div
              key={gw.id}
              onClick={() => handleGatewayChange(gw.id)}
              className={`relative cursor-pointer rounded-2xl p-4 border-2 transition-all duration-200 flex flex-col justify-between ${isSelected
                ? `${gw.borderActive} ${gw.bgLight} shadow-md scale-[1.02]`
                : `border-gray-200 bg-white ${gw.hoverBorder} hover:shadow-sm`
                }`}
            >
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="h-10 w-24 bg-white rounded-lg p-1 border border-gray-100 flex items-center justify-center shadow-xs">
                  <img
                    src={gw.logo}
                    alt={gw.name}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
                {isSelected && (
                  <span
                    className="h-6 w-6 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm"
                    style={{ backgroundColor: gw.color }}
                  >
                    ✓
                  </span>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-gray-900 text-sm">{gw.name}</span>
                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                    {count} added
                  </span>
                </div>
                <p className="text-[11px] text-gray-500 font-medium mt-0.5">{gw.tag}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Form Card */}
      <div className="bg-white border border-gray-200/80 rounded-2xl p-6 shadow-sm">
        <div className="flex items-center justify-between mb-5 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div
              className="h-8 w-8 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-xs"
              style={{ backgroundColor: getGatewayMeta(form.payment_type).color }}
            >
              <FiSmartphone className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900">
                {editingId ? `Edit ${getGatewayMeta(form.payment_type).name} Gateway` : `Add ${getGatewayMeta(form.payment_type).name} Gateway`}
              </h2>
              <p className="text-xs text-gray-500">
                {isFormCrypto
                  ? "Configure TRC20 wallet address and optional QR code for crypto deposits."
                  : "Configure account number, holder details, and optional QR code for user deposits."}
              </p>
            </div>
          </div>

          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg transition-colors"
            >
              <FiX className="h-3.5 w-3.5" />
              Cancel Edit
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Payment Gateway Type */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Payment Gateway <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full border border-gray-300 rounded-xl px-3.5 py-2.5 text-sm font-semibold text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                value={form.payment_type}
                onChange={(e) => handleGatewayChange(e.target.value)}
              >
                <option value="BKASH">bKash (BKASH)</option>
                <option value="NAGAD">Nagad (NAGAD)</option>
                <option value="ROCKET">Rocket (ROCKET)</option>
                <option value="UPAY">Upay (UPAY)</option>
                <option value="USDT">USDT (Crypto - TRC20)</option>
              </select>
            </div>

            {/* Account / RT Number or Wallet Address */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                {isFormCrypto ? "Recipient USDT (TRC20) Wallet Address" : "Recipient Wallet / RT Number"} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                className="w-full border border-gray-300 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all font-mono font-semibold"
                placeholder={isFormCrypto ? "e.g. TWXDgFtPsSTfbY3vzJ2RkmxrJiDufSkuvJ" : "e.g. 01700000000"}
                value={form.bank_account_number}
                onChange={(e) =>
                  setForm({ ...form, bank_account_number: e.target.value })
                }
              />
            </div>

            {/* Account Holder Name */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                {isFormCrypto ? "Wallet Label / Name" : "Account Holder Name"}
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                placeholder={isFormCrypto ? "e.g. Pori420 USDT TRC20" : "e.g. Pori420 Admin"}
                value={form.account_holder_name}
                onChange={(e) =>
                  setForm({ ...form, account_holder_name: e.target.value })
                }
              />
            </div>

            {/* Account Type / Channel Note */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                {isFormCrypto ? "Network / Channel Note" : "Account Type / Channel Note"}
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-xl px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all"
                placeholder={isFormCrypto ? "e.g. TRC20" : "e.g. bKash Personal, Agent, Merchant"}
                value={form.bank_name}
                onChange={(e) =>
                  setForm({ ...form, bank_name: e.target.value })
                }
              />
            </div>
          </div>

          {/* QR Code Upload Section */}
          <div className="bg-gray-50/70 border border-gray-200/80 rounded-xl p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-800 mb-0.5">
                  Upload QR Code Image <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <p className="text-xs text-gray-500">
                  {isFormCrypto
                    ? "Upload a USDT QR code screenshot so users can scan and pay directly from Binance / TrustWallet."
                    : "Upload a QR code screenshot so users can scan and pay directly from their bKash/Nagad app."}
                </p>
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto">
                <label className="cursor-pointer flex items-center justify-center gap-2 px-4 py-2 bg-white hover:bg-gray-50 border border-gray-300 rounded-xl text-xs font-semibold text-gray-700 transition-all shadow-xs">
                  <FiUploadCloud className="h-4 w-4 text-gray-600" />
                  <span>{preview ? "Change QR Image" : "Choose QR Image"}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>

                {preview && (
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="px-3 py-2 text-xs font-semibold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-xl transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            {preview && (
              <div className="mt-3 pt-3 border-t border-gray-200/60 flex items-center gap-3">
                <img
                  src={preview}
                  alt="QR Preview"
                  className="h-20 w-20 object-contain rounded-lg border border-gray-200 bg-white p-1 shadow-xs cursor-pointer hover:opacity-90"
                  onClick={() => setModalImage(preview)}
                />
                <div>
                  <p className="text-xs font-semibold text-gray-700">QR Code Preview</p>
                  <p className="text-[11px] text-gray-400">Click image to enlarge</p>
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={submitLoading}
              className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-gray-900 hover:bg-gray-800 text-white text-sm font-semibold transition-all shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitLoading ? (
                <>
                  <FiRefreshCw className="h-4 w-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <FiPlus className="h-4 w-4" />
                  <span>{editingId ? "Update Payment Gateway" : "Create Payment Gateway"}</span>
                </>
              )}
            </button>

            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                className="px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm font-semibold transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Methods Table Card */}
      <div className="bg-white border border-gray-200/80 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div>
            <h3 className="text-base font-bold text-gray-900">Active Gateways List</h3>
            <p className="text-xs text-gray-500">
              Manage configured accounts for BKASH, NAGAD, ROCKET, and UPAY.
            </p>
          </div>
          <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            {methods.length} Total Gateways
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50/80 text-gray-500 text-xs uppercase tracking-wider border-b border-gray-100">
              <tr>
                <th className="px-5 py-3.5 font-semibold">#</th>
                <th className="px-5 py-3.5 font-semibold">Gateway</th>
                <th className="px-5 py-3.5 font-semibold">Account Details</th>
                <th className="px-5 py-3.5 font-semibold">Type / Channel</th>
                <th className="px-5 py-3.5 font-semibold text-center">QR Code</th>
                <th className="px-5 py-3.5 font-semibold text-center">Status</th>
                <th className="px-5 py-3.5 font-semibold text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400">
                    <FiRefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-gray-400" />
                    <p className="text-sm">Loading payment methods...</p>
                  </td>
                </tr>
              ) : methods.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400">
                    <p className="text-sm font-medium text-gray-500">No payment gateways configured yet.</p>
                    <p className="text-xs text-gray-400 mt-1">Use the form above to add BKASH, NAGAD, ROCKET, or UPAY gateway.</p>
                  </td>
                </tr>
              ) : (
                methods.map((m, index) => {
                  const meta = getGatewayMeta(m.payment_type);
                  return (
                    <tr
                      key={m.id}
                      className={`hover:bg-gray-50/70 transition-colors ${editingId === m.id ? "bg-amber-50/50" : ""
                        }`}
                    >
                      <td className="px-5 py-4 text-xs font-mono text-gray-400">
                        {index + 1}
                      </td>

                      {/* Gateway Badge */}
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          {meta.logo ? (
                            <div className="h-9 w-16 bg-white rounded-lg p-1 border border-gray-200 flex items-center justify-center shadow-xs flex-shrink-0">
                              <img
                                src={meta.logo}
                                alt={meta.name}
                                className="max-h-full max-w-full object-contain"
                              />
                            </div>
                          ) : (
                            <div
                              className="h-9 w-9 rounded-lg flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
                              style={{ backgroundColor: meta.color }}
                            >
                              {meta.name.slice(0, 2).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <span className="font-bold text-gray-900 block text-sm">
                              {meta.name}
                            </span>
                            <span className="text-[11px] text-gray-400 font-medium">
                              {m.currency || "BDT"}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Account details */}
                      <td className="px-5 py-4">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono font-bold text-gray-900 text-sm">
                              {m.bank_account_number || "—"}
                            </span>
                            {m.bank_account_number && (
                              <button
                                type="button"
                                onClick={() => handleCopy(m.bank_account_number, m.id)}
                                className="text-gray-400 hover:text-gray-700 p-1 rounded transition-colors"
                                title="Copy Number"
                              >
                                {copiedId === m.id ? (
                                  <FiCheck className="h-3.5 w-3.5 text-green-600" />
                                ) : (
                                  <FiCopy className="h-3.5 w-3.5" />
                                )}
                              </button>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            Holder: <span className="font-medium text-gray-700">{m.account_holder_name || "Pori420 Admin"}</span>
                          </p>
                        </div>
                      </td>

                      {/* Type / Channel */}
                      <td className="px-5 py-4">
                        <span className="inline-block px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                          {m.bank_name || `${meta.name} Personal`}
                        </span>
                      </td>

                      {/* QR Image */}
                      <td className="px-5 py-4 text-center">
                        {m.qr_image ? (
                          <div className="inline-block relative group">
                            <img
                              src={m.qr_image}
                              alt="QR Code"
                              className="h-10 w-10 object-contain rounded-lg border border-gray-200 bg-white p-0.5 cursor-pointer shadow-xs transition-transform group-hover:scale-105"
                              onClick={() => setModalImage(m.qr_image)}
                            />
                            <button
                              type="button"
                              onClick={() => setModalImage(m.qr_image)}
                              className="absolute inset-0 bg-black/40 text-white rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                              title="View Full QR"
                            >
                              <FiEye className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-5 py-4 text-center">
                        <button
                          type="button"
                          onClick={() => toggleStatus(m.id, !m.is_active)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all ${m.is_active
                            ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200 border border-emerald-200"
                            : "bg-rose-100 text-rose-800 hover:bg-rose-200 border border-rose-200"
                            }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${m.is_active ? "bg-emerald-600" : "bg-rose-600"
                              }`}
                          />
                          {m.is_active ? "Active" : "Disabled"}
                        </button>
                      </td>

                      {/* Actions */}
                      <td className="px-5 py-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleEdit(m)}
                            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Edit Gateway"
                          >
                            <FiEdit2 className="h-4 w-4" />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDelete(m.id)}
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete Gateway"
                          >
                            <FiTrash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lightbox / Modal for QR Code Full View */}
      {modalImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4"
          onClick={() => setModalImage(null)}
        >
          <div
            className="bg-white rounded-2xl p-5 max-w-sm w-full shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-gray-900">QR Code Preview</h4>
              <button
                type="button"
                onClick={() => setModalImage(null)}
                className="text-gray-400 hover:text-gray-700 p-1 rounded-lg"
              >
                <FiX className="h-5 w-5" />
              </button>
            </div>
            <div className="bg-gray-50 border rounded-xl p-3 flex items-center justify-center">
              <img
                src={modalImage}
                alt="QR Code Enlarge"
                className="max-h-72 w-auto object-contain rounded"
              />
            </div>
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => window.open(modalImage, "_blank")}
                className="text-xs font-semibold text-gray-700 hover:text-gray-900 underline"
              >
                Open in new tab
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
